import { ref, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import api from '@/utils/api';

export function useRescan() {
  const authStore = useAuthStore();
  const showRescanModal = ref(false);
  const rescanningInvoiceId = ref(null);
  const rescanLoading = ref(false);
  const rescanError = ref(null);

  // Hard scan availability
  const hardScansRemaining = computed(() => {
    const user = authStore.user;
    if (!user?.subscription) return 0;
    const used = user.subscription.hard_scans_used || 0;
    const limit = user.subscription.hard_scan_limit || 0;
    return Math.max(0, limit - used);
  });

  const hardScanLimit = computed(() => {
    const user = authStore.user;
    return user?.subscription?.hard_scan_limit || 0;
  });

  const canUseHardScan = computed(() => {
    return hardScansRemaining.value > 0;
  });

  // Open rescan modal
  const openRescanModal = (invoiceId) => {
    rescanningInvoiceId.value = invoiceId;
    showRescanModal.value = true;
    rescanError.value = null;
  };

  // Close rescan modal
  const closeRescanModal = () => {
    showRescanModal.value = false;
    rescanningInvoiceId.value = null;
    rescanError.value = null;
  };

  // Perform rescan
  const performRescan = async (invoiceId, useHardScan = false) => {
    if (!invoiceId) {
      invoiceId = rescanningInvoiceId.value;
    }
    
    if (!invoiceId) return null;

    // Check hard scan availability
    if (useHardScan && !canUseHardScan.value) {
      rescanError.value = 'No hard scans remaining';
      return null;
    }

    rescanLoading.value = true;
    rescanError.value = null;

    try {
      const response = await api.post(`/api/invoices/${invoiceId}/rescan`, {
        hard_scan: useHardScan
      });

      rescanLoading.value = false;
      closeRescanModal();
      
      // Refresh user data to update hard scan count
      await authStore.fetchUser();
      
      return response.data;
    } catch (error) {
      rescanLoading.value = false;
      rescanError.value = error.response?.data?.message || 'Failed to rescan invoice';
      console.error('Rescan error:', error);
      return null;
    }
  };

  return {
    showRescanModal,
    rescanningInvoiceId,
    rescanLoading,
    rescanError,
    hardScansRemaining,
    hardScanLimit,
    canUseHardScan,
    openRescanModal,
    closeRescanModal,
    performRescan
  };
}
